import React, { useState, useEffect, useCallback } from 'react';
import { transactionService } from '../transactionService';
import './SaleSection.css';
import FetchWallet from './FetchWallet';
import { track } from '@vercel/analytics';


// New component for the progress bar and remaining NFTs count
function NFTProgress({ remainingNFTs, totalNFTs }) {
  const soldPercentage = ((totalNFTs - remainingNFTs) / totalNFTs) * 100;

  return (
    <div>
      <div className="progress-bar-container">
        <div className="progress-bar-fill" style={{ width: `${soldPercentage}%` }}></div>
        <div className="progress-text">
          {soldPercentage.toFixed(1)}%
        </div>
      </div>
      <p className="nft-remaining">
        {remainingNFTs} / {totalNFTs} NFTs remaining
      </p>
    </div>
  );
}

function SaleSection({ connected, accountAddress, walletData, rdt }) {
  const [price, setPrice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [amount, setAmount] = useState(1);
  const [totalPrice, setTotalPrice] = useState(null);
  const [buySuccess, setBuySuccess] = useState(false);
  const [transactionStatus, setTransactionStatus] = useState(null);
  const [remainingNFTs, setRemainingNFTs] = useState(0);
  const TOTAL_NFTS = 249;
  const componentAddress = 'component_rdx1czvhgwq69vhhtv85g5g878elx8942s0t64r8tskuj74fz77w726hxs';
  const MAX_AMOUNT = 10;

  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    console.log('SaleSection: Connected status:', connected);
    console.log('SaleSection: Account address:', accountAddress);
    console.log('SaleSection: Wallet data:', walletData);

    if (connected && accountAddress) {
      fetchComponentState();
      const interval = setInterval(fetchComponentState, 20000);
      return () => clearInterval(interval);
    }
  }, [connected, accountAddress]);

  useEffect(() => {
    if (price !== null && amount !== '') {
      setTotalPrice((parseFloat(price) * parseInt(amount)).toFixed(2));
    } else {
      setTotalPrice(null);
    }
  }, [price, amount]);



  const fetchComponentState = async () => {
    try {
      const response = await fetch('https://mainnet.radixdlt.com/state/entity/details', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ addresses: [componentAddress] }),
      });

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const data = await response.json();
      if (data.items && data.items.length > 0) {
        const componentState = data.items[0].details.state;
        const priceField = componentState.fields.find(field => field.field_name === 'price');
        if (priceField && priceField.value) {
          setPrice(priceField.value);
          setTotalPrice((parseFloat(priceField.value) * amount).toFixed(2));
        }

        const nftResource = data.items[0].non_fungible_resources.items[0];
        if (nftResource && nftResource.amount) {
          setRemainingNFTs(parseInt(nftResource.amount));
        }
      } else {
        throw new Error('Component state not found');
      }
    } catch (err) {
      console.error('Error fetching component state:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAmountChange = (e) => {
    const value = e.target.value;
    if (value === '' || (parseInt(value) >= 0 && parseInt(value) <= MAX_AMOUNT && !isNaN(parseInt(value)))) {
      setAmount(value);
    }
  };

  const handleAmountBlur = () => {
    if (amount === '' || parseInt(amount) < 1) {
      setAmount(1);
    } else if (parseInt(amount) > MAX_AMOUNT) {
      setAmount(MAX_AMOUNT);
    } else {
      setAmount(parseInt(amount));
    }
  };

  const handleBuy = async () => {
    console.log('Buy button clicked');
    console.log('SaleSection: Connected status:', connected);
    console.log('SaleSection: Account address:', accountAddress);

    if (!connected || !accountAddress) {
      console.error('Wallet not connected');
      setTransactionStatus('Wallet not connected. Please connect your wallet.');
      return;
    }

    console.log(`Buying ${amount} NFT(s) at ${price} XRD each. Total: ${totalPrice} XRD`);
    console.log('Account address:', accountAddress);

    try {
      setTransactionStatus('Processing transaction...');
      console.log('Calling transactionService.sendTransaction...');
      const transactionResult = await transactionService.sendTransaction(accountAddress, amount, price);

      console.log('Transaction result:', transactionResult);

      setBuySuccess(true);
      setTransactionStatus('Transaction successful!');
      setAmount(1);
      setTimeout(() => {
        setBuySuccess(false);
        setTransactionStatus(null);
      }, 5000);

    } catch (error) {
      console.error('Error processing purchase:', error);

      if (error.code === 'USER_REJECTED' ||
          error.message === 'Transaction rejected by user' ||
          error.error === 'rejectedByUser') {
        setTransactionStatus('Transaction rejected by user.<br />Please try again if you want to proceed.');
      } else if (error.message?.includes('Sale is not allowed yet') ||
                 error.message?.includes('PanicMessage("[Buy]: Sale is not allowed yet')) {
        setTransactionStatus('Sale has not started yet.<br />Please check back later.');
      } else {
        setTransactionStatus('Transaction failed.<br />Please try again.');
      }

      setTimeout(() => {
        setTransactionStatus(null);
      }, 5000);
    }
  };

  const handleSetMaxAmount = () => {
    setAmount(MAX_AMOUNT);
  };

  if (!connected || !accountAddress) {
    return (
      <div id="sale" className="sale-content">
        <div className="sale-container wallet-connect-message">
          <p>Please connect your Radix wallet to buy NFTs.</p>
        </div>
      </div>
    );
  }

  if (loading) return (
    <div id="sale" className="sale-content">
      <div className="sale-container loading-message">
        <div className="loading-spinner"></div>
        <p>Loading...</p>
      </div>
    </div>
  );
  if (error) return <div id="sale" className="sale-content"><div className="error-message">Error: {error}</div></div>;

  return (
    <section id="sale" className="page-content sale-content">
      <div className="sale-container">
        <img
          src={`${process.env.PUBLIC_URL}/images/capytitlebar.png`}
          alt="CAPYCLUB"
          className="sale-title-image"
        />
        {loading ? (
          <p>Loading...</p>
        ) : error ? (
          <p>Error: {error}</p>
        ) : (
          <>
            <NFTProgress remainingNFTs={remainingNFTs} totalNFTs={TOTAL_NFTS} />
            <p>Total Price: {totalPrice !== null ? `${totalPrice} XRD` : 'N/A'}</p>
            <div className="amount-input-container">
              <div className="amount-input">
                <label htmlFor="amount">Amount:</label>
                <input
                  type="number"
                  id="amount"
                  min="1"
                  max={MAX_AMOUNT}
                  value={amount}
                  onChange={handleAmountChange}
                  onBlur={handleAmountBlur}
                  disabled={true}
                />
                <button
                  onClick={handleSetMaxAmount}
                  className="max-button"
                  disabled={true}
                >
                  Max
                </button>
              </div>
            </div>
            <div className="buy-button-container">
              <button
                onClick={handleBuy}
                disabled={true}
                className={`buy-button ${buySuccess ? 'buy-button-success' : 'buy-button-normal'} ${isHovered ? 'buy-button-hover' : ''}`}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
              >
                SOLD OUT
                <span className="buy-button-ripple" />
              </button>
            </div>
            {transactionStatus && <p dangerouslySetInnerHTML={{ __html: transactionStatus }} />}
            {connected && accountAddress && (
              <div className="wallet-balance-wrapper">
                <FetchWallet accountAddress={accountAddress} />
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
}

export default SaleSection;
